import { Fragment, useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { useAction, useMutation } from "convex/react";
import {
  Badge,
  Box,
  Button,
  ButtonGroup,
  CloseButton,
  Collapsible,
  Dialog,
  Field,
  FileUploadHiddenInput,
  FileUploadList,
  FileUploadRoot,
  FileUploadTrigger,
  Flex,
  Heading,
  HStack,
  Icon,
  IconButton,
  Input,
  NativeSelect,
  Portal,
  Skeleton,
  Slider,
  Stack,
  Switch,
  Text,
  Textarea,
  Timeline,
} from "@chakra-ui/react";
import {
  LuChevronDown,
  LuCircleCheckBig,
  LuClock3,
  LuFileDown,
  LuFileText,
  LuLockKeyhole,
  LuMessageSquareWarning,
  LuSave,
  LuTrash2,
  LuUndo2,
  LuUpload,
} from "react-icons/lu";
import type { Id } from "@convex/_generated/dataModel";
import { api } from "@convex/_generated/api";
import {
  ReviewFormFields,
  missingRequiredReviewFields,
  type ReviewFieldValue,
} from "@/components/ReviewFormFields";
import { readableError } from "@/lib/errors";
import { useQuery } from "@/lib/convex";
import { toastError } from "../components/ui/toaster";
import { RECORD_ENABLED } from "@/lib/features";
import { TextLink } from "../components/TextLink";
import { useEffectivePersonId } from "../impersonation";
import { useViewerAccess } from "@/lib/access";
import { TaskPeopleDialog } from "./TaskPeopleDialog";
import { createColumnHelper } from "@tanstack/react-table";
import { DataTable } from "../components/DataTable";
import { LuShieldUser } from "../components/icons";
import { Tooltip } from "../components/ui/tooltip";
import { statusPresentation } from "../components/activityStatus";
import { participationRoleLabel } from "./eventsShared";
import {
  formatDay,
  formatDayTime,
  toResponseData,
  ActivityRefs,
  TaskRefs,
  TaskFileField,
  asFileList,
  fileIdentity,
  isFileValue,
  stepWaitsOnViewer,
  type AssignmentTask,
  type FieldDefinition,
  uploadWithRetry,
  stripDownloadUrl,
  type FieldValue,
  type FileResponseValue,
  type ReviewerSpecValue,
} from "./ActivityDetailParts";

/**
 * One task of an activity: its form, its files, and the decision on it.
 *
 * A component because all of its state is its own. While the draft answers,
 * the pending files and the flashes lived on the page in maps keyed by task
 * id, typing one character re-rendered every other task, the dependency list
 * and the whole history.
 */
export function ActivityTaskCard({
  task,
  todayDay,
  activityClosed,
  subjectName,
}: {
  task: AssignmentTask;
  todayDay: string;
  activityClosed: boolean;
  /** Whose record this activity is. Null when it is the viewer's own. */
  subjectName: string | null;
}) {
  const effectivePersonId = useEffectivePersonId();
  const access = useViewerAccess();
  const [editingPeople, setEditingPeople] = useState(false);
  // A finished step is one line until somebody wants its answers back; so is
  // one that cannot be started yet. Everything else is open.
  const [open, setOpen] = useState(() => task.status !== "approved" && !task.locked);
  const saveTaskDraft = useMutation(api.personActivities.saveTaskDraft);
  const submitTask = useMutation(api.personActivities.submitTask);
  const withdrawSubmission = useMutation(api.personActivities.withdrawSubmission);
  const completeTaskAsActor = useMutation(api.personActivities.completeTaskAsActor);
  const approveTask = useMutation(api.reviews.approveTask);
  const requestTaskRevision = useMutation(api.reviews.requestTaskRevision);
  const generateUploadUrl = useAction(api.azureFiles.generateUploadUrl);
  const syncUploadedFile = useMutation(api.azureFiles.syncUploadedFile);
  const deleteFile = useMutation(api.azureFiles.deleteFile);

  // All of this belonged to the page, keyed by task id, so a keystroke in one
  // task re-rendered every other one along with the dependency list and the
  // history. It is one task's business and now lives with the task.
  const [draft, setDraft] = useState<Record<string, FieldValue>>({});
  // Files picked but not saved. They go to Azure as part of save or submit, so
  // a blob only ever exists because a save created it -- abandoning the form
  // cannot leave anything behind.
  const [pendingByField, setPendingByField] = useState<Record<string, File[]>>({});
  const [saving, setSaving] = useState(false);
  // Submitting hands the work to somebody else, and withdrawing takes it back
  // from them. Both are visible to another person the moment they happen, and
  // neither is undone by clicking again, so both ask first.
  const [confirming, setConfirming] = useState<"submit" | "withdraw" | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  // The browser's own wording, shown quietly beneath the sentence above.
  const [flashDetail, setFlashDetail] = useState<string | null>(null);
  // Field-level feedback belongs next to the field it refers to, not down by
  // the save and submit buttons where the task-level flash lives.
  const [flashByFieldKey, setFlashByFieldKey] = useState<Record<string, string | null>>({});
  const [openDecision, setOpenDecision] = useState<{ key: string; mode: "approve" | "revise" } | null>(null);
  const [decisionComment, setDecisionComment] = useState("");
  const [decisionValues, setDecisionValues] = useState<Record<string, ReviewFieldValue>>({});


  function setPendingFiles(fieldKey: string, files: File[]) {
    setPendingByField((current) => ({ ...current, [fieldKey]: files }));
  }


  // Uploads this task's pending files and returns the resulting field values,
  // plus the keys created so they can be cleaned up if the save itself fails.
  async function uploadPendingFiles(task: AssignmentTask): Promise<{
    values: Record<string, FieldValue>;
    uploadedKeys: string[];
  }> {
    const taskId = String(task._id);
    const values: Record<string, FieldValue> = {};
    const uploadedKeys: string[] = [];

    for (const field of task.formSchema?.fields ?? []) {
      if (field.type !== "file") {
        continue;
      }
      const pending = pendingByField[field.key] ?? [];
      if (pending.length === 0) {
        continue;
      }

      const existing = asFileList(
        getFieldValue(field.key, (task.responseData?.[field.key] ?? null) as FieldValue),
      );
      const uploaded: FileResponseValue[] = [];

      for (const file of pending) {
        // Straight to Azure Blob with a short-lived SAS URL, so the bytes never
        // pass through Convex. The blob-type header is required by Azure.
        // A fresh link per attempt, not one link retried: see
        // `uploadWithRetry`. The bytes never pass through Convex either way.
        const key = await uploadWithRetry(file, () => generateUploadUrl({}));

        // Register the blob so it is tracked and not treated as an orphan.
        await syncUploadedFile({ key, fileName: file.name });
        uploadedKeys.push(key);
        uploaded.push({
          key,
          fileName: file.name,
          mimeType: file.type || null,
          size: file.size,
        });
      }

      values[field.key] = field.multiple ? [...existing, ...uploaded] : uploaded[0] ?? null;
    }

    return { values, uploadedKeys };
  }

  // Detaches an already-stored file. Its blob is deleted server-side when the
  // change is saved (deleteSupersededTaskFiles), so abandoning the edit leaves the
  // file intact. Files merely picked and not yet uploaded live in the picker's own
  // list and are dropped there, with no blob to clean up.
  function removeFile(
    taskId: string,
    field: FieldDefinition,
    files: FileResponseValue[],
    target: FileResponseValue,
  ) {
    const remaining = files.filter((file) => fileIdentity(file) !== fileIdentity(target));
    const nextValue: FieldValue = field.multiple ? remaining : remaining[0] ?? null;
    setDraft((current) => ({ ...current, [field.key]: nextValue }));
    setFlashByFieldKey((current) => ({
      ...current,
      [field.key]: `Removed ${target.fileName}. Save or submit to apply.`,
    }));
  }

  // A draft entry may legitimately be null -- that is how "file removed" is
  // expressed -- so test for the key's presence rather than using ??, which
  // would silently fall back to the stored value and lose the removal.
  function getFieldValue(fieldKey: string, fallback: FieldValue) {
    if (fieldKey in draft) {
      return draft[fieldKey] as FieldValue;
    }
    return fallback;
  }

  function buildResponseData(
    task: AssignmentTask,
    uploadedValues: Record<string, FieldValue> = {},
  ) {
    const taskDraft = draft[String(task._id)];
    return Object.fromEntries(
      task.formSchema?.fields.map((field) => {
        // Freshly uploaded files win; then the draft -- where a present-but-null
        // entry is a removal, so it must not be coalesced away; then the stored
        // value.
        const value = field.key in uploadedValues
          ? uploadedValues[field.key]
          : field.key in draft
            ? draft[field.key]
            : ((task.responseData?.[field.key] ?? null) as FieldValue);
        return [field.key, stripDownloadUrl(value as FieldValue)];
      }) ?? [],
    );
  }

  // Drop the local draft once the server has them, so the task re-renders from
  // the query -- which is what carries the download URLs. Without this the UI
  // keeps showing the pre-save draft until a page reload.
  // Drop the local draft once the server has it, so the card re-renders from
  // the query -- which is what carries the download URLs. Without this the UI
  // keeps showing the pre-save draft until a reload.
  function clearTaskDrafts() {
    setDraft({});
    setFlashByFieldKey({});
    setPendingByField({});
  }

  // Single path for persisting a task: upload the files picked for it, then write
  // the response. If the write fails the just-created blobs are referenced by
  // nothing, so they are removed rather than left for the reconciler.
  async function persistTask(
    task: AssignmentTask,
    write: (responseData: ReturnType<typeof buildResponseData>) => Promise<unknown>,
    successMessage: string,
    failureMessage: string,
  ) {
    const taskId = String(task._id);
    setSaving(true);
    setFlash(null);
    setFlashDetail(null);
    let uploadedKeys: string[] = [];

    try {
      const uploads = await uploadPendingFiles(task);
      uploadedKeys = uploads.uploadedKeys;
      await write(buildResponseData(task, uploads.values));
      clearTaskDrafts();
      setFlash(successMessage);
    } catch (error) {
      for (const key of uploadedKeys) {
        void deleteFile({ key });
      }
      // What to do, then what went wrong. The original text is kept --
      // somebody has to be able to report it -- but it is no longer the
      // first thing read, and on its own it told the reader nothing.
      const readable = readableError(error, failureMessage);
      setFlash(readable.message);
      setFlashDetail(readable.detail ?? null);
    } finally {
      setSaving(false);
    }
  }

  async function handleSave(task: AssignmentTask) {
    await persistTask(
      task,
      (responseData) => saveTaskDraft({ taskId: task._id, responseData, effectivePersonId }),
      "Draft saved.",
      "Saving draft failed.",
    );
  }

  // Not through persistTask: there is nothing to write. Taking a submission
  // back must not also save whatever happens to be in the form, or a
  // withdrawal would quietly become an edit.
  async function handleWithdraw(task: AssignmentTask) {
    setSaving(true);
    setFlash(null);
    setFlashDetail(null);
    try {
      await withdrawSubmission({ taskId: task._id, effectivePersonId });
      setFlash("Withdrawn. You can change it and submit again.");
    } catch (error) {
      const readable = readableError(error, "Withdrawing failed.");
      setFlash(readable.message);
      setFlashDetail(readable.detail ?? null);
    } finally {
      setSaving(false);
    }
  }

  async function handleSubmit(task: AssignmentTask) {
    await persistTask(
      task,
      (responseData) => submitTask({ taskId: task._id, responseData, effectivePersonId }),
      "Step submitted.",
      "Submitting the step failed.",
    );
  }

  async function handleCompleteAsActor(task: AssignmentTask) {
    const needsReview = task.reviewMode !== null;
    await persistTask(
      task,
      (responseData) => completeTaskAsActor({
        taskId: task._id,
        responseData,
        effectivePersonId: effectivePersonId as Id<"persons"> | null,
      }),
      needsReview ? "Submitted for approval." : "Step completed.",
      "Completing the step failed.",
    );
  }

  function setFieldDraft(fieldKey: string, value: FieldValue) {
    setDraft((current) => ({ ...current, [fieldKey]: value }));
  }

  async function submitDecision(
    taskId: Id<"personActivityTasks">,
    spec: ReviewerSpecValue,
    reviewFields: FieldDefinition[],
  ) {
    const mode = openDecision?.mode ?? "approve";
    // A revision request without a reason is useless to whoever has to act on
    // it. An approval needs no words.
    if (mode === "revise" && decisionComment.trim() === "") {
      setFlash("Say what needs changing.");
      return;
    }

    // Only an approval records the assessment, so only an approval has to
    // have answered it.
    if (mode === "approve" && reviewFields.length > 0) {
      const missing = missingRequiredReviewFields(reviewFields, decisionValues);
      if (missing.length > 0) {
        setFlash(`Still to answer: ${missing.join(", ")}.`);
        return;
      }
    }

    try {
      const args = {
        taskId,
        reviewerSpec: spec,
        comment: decisionComment.trim(),
        effectivePersonId: effectivePersonId as Id<"persons"> | null,
      };
      if (mode === "approve") {
        await approveTask(
          reviewFields.length > 0
            ? { ...args, responseData: toResponseData(decisionValues) }
            : args,
        );
      } else {
        await requestTaskRevision(args);
      }
      setOpenDecision(null);
      setDecisionComment("");
      setDecisionValues({});
    } catch (error) {
      setFlash(error instanceof Error ? error.message : "The decision could not be recorded.");
    }
  }

  const taskId = String(task._id);
  // Somebody sees the form if they are the one who fills it
  // in: the assignee on their own task, or whoever the actor
  // resolves to on one performed by another.
  // Seeing the form and being allowed to write it are two
  // different questions, and conflating them was the bug: a
  // reviewer needs to READ what was submitted in order to sign
  // it, and was handed an editable copy instead.
  const showForm = !task.locked && task.formSchema
    // Without this a supervisor looking at a student's
    // unsubmitted draft saw an empty form and could not
    // tell it from an unanswered one.
    && task.contentVisible;
  const needsReview = task.reviewMode !== null;
  // Editable, or merely legible. The backend refuses a write
  // to an approved step or to a closed activity, so rendering
  // live inputs invited an edit that could never be saved --
  // and the buttons were already gone, which made a typed
  // answer disappear with nothing to say why.
  // The backend is the authority on both: `viewerCanPerform`
  // is false for anybody who is not the assignee (or the actor,
  // where the step is somebody else's), and false once the work
  // is submitted or approved.
  const readOnly = !task.viewerCanPerform || activityClosed;

  // Back in the assignee's hands after being submitted, and
  // not because a reviewer sent it back. `submittedAt` survives
  // a withdrawal on purpose -- it decides which signatures
  // count on the next submission -- so it cannot be read as
  // "this is submitted", which is how the footer came to say
  // "Submitted 23:08" about work that had been taken back at
  // 23:09.
  const withdrawn = task.status === "draft"
    && task.submittedAt !== null
    && task.lastRevisionRequestedAt === null;

  // What the reviewer asked for, at the top of the step it is about: this is
  // where it gets acted on. It was in a card of its own above all the steps.
  const sentBack = task.status === "needs_revision"
    ? task.reviews.filter((review) => review.decision === "revision_requested").at(-1) ?? null
    : null;

  // An admin's repair for this one copy: the named reviewer
  // left, or this step belongs to somebody else here. Not on a
  // finished step. An action, so it sits with the step's other
  // actions, and it carries the role marker the navigation
  // uses: the viewer has it because of a role, not because the
  // step is theirs.
  const changePeople = access?.isActivityAdmin && !activityClosed && task.status !== "approved" ? (
    <Tooltip content="Activity admin">
      <Button size="sm" variant="ghost" ms="auto" onClick={() => setEditingPeople(true)}>
        <LuShieldUser />
        Change people
      </Button>
    </Tooltip>
  ) : null;
  // The step's state on the timeline, in the same four colours as an
  // activity's. Its own deadline outranks the rest, as on the activity.
  const overdue = task.dueDay !== null && task.dueDay < todayDay && task.status !== "approved";
  const look = task.locked
    ? statusPresentation("blocked")
    : statusPresentation(STEP_PRESENTATION[task.status], overdue);
  const finishedOn = task.status === "approved"
    ? task.reviews.filter((review) => review.decision === "approved").at(-1)?.createdAt ?? task.submittedAt
    : null;

  // Waiting on the person looking at it: the whole step says so, not a
  // sentence inside it. Not on a closed activity, where nothing can be done.
  const yours = stepWaitsOnViewer(task) && !activityClosed;

  // What the step is, beside its title, in one muted style. Required is said
  // in words on every step: it is the usual case, and still worth reading.
  // Where it stands goes on its own line underneath, in its colour.
  const facts: ReactNode[] = [
    task.required ? "Required" : "Optional",
    task.performedByOther ? `${task.actorLabel} fills this in` : null,
    // Nothing when nobody signs: the absence says it.
    task.reviewersPhrase === null ? null : task.reviewSummaryLabel,
    // Only where the step has a deadline of its own.
    task.dueDay === null ? null : (
      <Text as="span" color={overdue ? "orange.fg" : undefined}>Due {formatDay(task.dueDay)}</Text>
    ),
  ].filter((fact) => fact !== null);

  return (
    <Timeline.Item id={`task-${task._id}`} scrollMarginTop="20">
      <Timeline.Connector>
        <Timeline.Separator />
        {/* Filled for a required step, dashed for an optional one: the
            words say it too, this is what says it at a glance. */}
        <Timeline.Indicator
          mt={4}
          colorPalette={look.tone}
          // The root's solid variant fills it; an optional step is hollow.
          bg={task.required ? undefined : "bg"}
          borderStyle={task.required ? undefined : "dashed"}
          borderWidth={task.required ? undefined : "2px"}
          borderColor={task.required ? undefined : "colorPalette.solid"}
          color={task.required ? undefined : "colorPalette.fg"}
          aria-label={`${task.locked ? "Locked" : task.statusLabel}, ${task.required ? "required" : "optional"}`}
        >
          <Icon as={look.icon} />
        </Timeline.Indicator>
      </Timeline.Connector>
      <Timeline.Content>
        <Collapsible.Root open={open} onOpenChange={(event) => setOpen(event.open)}>
          <Box
            position="relative"
            borderWidth={yours ? "2px" : "1px"}
            borderColor={yours ? "blue.solid" : "border"}
            shadow={yours ? "md" : undefined}
            bg="bg"
          >
            {/* A tab on the border rather than a badge by the title: the
                label and the frame are one mark, so where to act cannot be
                missed. Square, to meet the border's corner. */}
            {yours ? (
              <Badge
                position="absolute"
                top="-2px"
                left="-2px"
                colorPalette="blue"
                variant="solid"
                borderRadius="0"
                px={2.5}
                py={1}
              >
                Your turn
              </Badge>
            ) : null}
            {/* The whole header opens and closes the step, not only the
                chevron: it is the biggest target on the page and the one
                people reach for. A button in all but tag, for the keyboard. */}
            <Flex
              role="button"
              tabIndex={0}
              aria-expanded={open}
              onClick={() => setOpen(!open)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  setOpen(!open);
                }
              }}
              cursor="pointer"
              align="flex-start"
              justify="space-between"
              gap={4}
              px={5}
              pt={yours ? 9 : 5}
              pb={5}
            >
              <Stack gap={1} minW={0}>
                <HStack gap={3} align="baseline" flexWrap="wrap" rowGap={0}>
                  <Heading size="md">{task.titleSnapshot}</Heading>
                  <Text fontSize="sm" color="fg.muted">
                    {facts.map((fact, index) => (
                      <Fragment key={index}>
                        {index > 0 ? " · " : null}
                        {fact}
                      </Fragment>
                    ))}
                  </Text>
                </HStack>
                {task.locked ? null : (
                  <Text fontSize="sm" color={`${look.tone}.fg`}>
                    {overdue ? "Overdue" : task.statusLabel}
                    {finishedOn === null ? "" : ` ${formatDate(finishedOn)}`}
                  </Text>
                )}
                {task.locked ? (
                  // The step named here is a link that scrolls to it; following
                  // it should not also fold this one.
                  <HStack gap={1.5} color="fg.muted" onClick={(event) => event.stopPropagation()}>
                    <Icon as={LuLockKeyhole} boxSize={3.5} />
                    <Text fontSize="sm">
                      {task.blockedBy.length > 0 ? (
                        <>
                          Opens once <TaskRefs items={task.blockedBy} />{" "}
                          {task.blockedBy.length === 1 ? "is" : "are"} approved.
                        </>
                      ) : (
                        "Opens once an earlier step is approved."
                      )}
                    </Text>
                  </HStack>
                ) : null}
              </Stack>
              {/* Only a sign now: the header is what is clicked. */}
              <Collapsible.Indicator
                aria-hidden
                color="fg.muted"
                mt={1}
                transition="transform 0.15s"
                _open={{ transform: "rotate(180deg)" }}
              >
                <LuChevronDown />
              </Collapsible.Indicator>
            </Flex>

            <Collapsible.Content>
              {/* Everything but the header on the page's grey, so the
                  header reads as the step's title bar and the rest as its
                  content. Fields stay white, as places to write. */}
              <Stack
                gap={4}
                p={5}
                bg="bg.subtle"
                borderTopWidth="1px"
                borderColor="border"
                css={{ "& input, & textarea, & select": { background: "var(--chakra-colors-bg)" } }}
              >
                {sentBack ? (
                  <Box borderWidth="1px" borderColor="orange.muted" bg="orange.subtle" px={4} py={3}>
                    <HStack gap={2}>
                      <Icon as={LuMessageSquareWarning} boxSize={4} color="orange.fg" />
                      <Text fontSize="sm" fontWeight="medium">
                        Revision requested by {sentBack.reviewerName ?? "a reviewer no longer at CeMM"}
                        {" "}({reviewerRoleLabel(sentBack)}), {formatDayTime(sentBack.createdAt)}
                      </Text>
                    </HStack>
                    {sentBack.comment ? <Text mt={1} fontSize="sm">{sentBack.comment}</Text> : null}
                  </Box>
                ) : null}
                {task.coSupervisorMissing && task.status !== "approved" ? (
                  <Box borderWidth="1px" borderColor="orange.muted" bg="orange.subtle" px={4} py={3}>
                    <Text fontSize="sm">
                      No co-supervisor is recorded yet, so nobody can do this step as the co-supervisor.
                      The supervisor or an activity admin records one on the programme page.
                    </Text>
                  </Box>
                ) : null}
                {task.descriptionSnapshot ? (
                  // Body text, not the facts line's muted grey: it is what the
                  // step asks, and reads as such.
                  <Text fontSize="sm">{task.descriptionSnapshot}</Text>
                ) : null}
                {access?.isActivityAdmin ? (
                  <TaskPeopleDialog task={task} open={editingPeople} onClose={() => setEditingPeople(false)} />
                ) : null}

        {/* Completed by a record rather than by work. There is
            no form and nothing to submit, so without this the
            step would show as approved with no account of why. */}
        {task.participationCriteria !== undefined ? (
          <CitedRecordBlock task={task} />
        ) : null}

        {/* Somebody else's draft: listed, but not readable
            until they submit it (model §22). */}
        {!task.locked && !task.contentVisible ? (
          <Text fontSize="sm" color="fg.muted">
            {task.performedByOther
              ? `${task.actorLabel} has not submitted this yet.`
              : "Not submitted yet. Drafts are only visible to the person writing them."}
          </Text>
        ) : null}

        {/* Performed by somebody else: read-only for everybody but them */}
        {!task.locked && task.contentVisible && task.performedByOther && !task.viewerCanPerform && task.status !== "approved" ? (
          <Text fontSize="sm" color="fg.muted">
            {task.actorLabel} fills this in. There is nothing for you to do here.
          </Text>
        ) : null}

        {/* Show form: for assignee-mode tasks (to assignee), or reviewer-mode tasks (to reviewer) */}
        {showForm && task.formSchema ? (
          <Stack gap={5}>
            {task.formSchema.fields.map((field) => {
              const currentValue = getFieldValue(field.key, task.responseData?.[field.key] ?? null);

              if (field.type === "boolean") {
                return (
                  <Field.Root key={field.key}>
                    <Switch.Root
                      disabled={readOnly}
                      colorPalette="blue"
                      checked={Boolean(currentValue ?? false)}
                      onCheckedChange={(e) => setFieldDraft(field.key, e.checked)}
                    >
                      <Switch.HiddenInput />
                      <Switch.Control />
                      <Switch.Label>
                        {field.label}
                        {field.required ? <Text as="span" ml={1} color="red.500">*</Text> : null}
                      </Switch.Label>
                    </Switch.Root>
                    {field.description ? (
                      <Field.HelperText>{field.description}</Field.HelperText>
                    ) : null}
                  </Field.Root>
                );
              }

              return (
                <Field.Root key={field.key} required={field.required}>
                  <Field.Label>
                    {field.label}
                    <Field.RequiredIndicator />
                  </Field.Label>
                  {field.description ? (
                    <Text mb={1.5} fontSize="xs" color="fg.muted">{field.description}</Text>
                  ) : null}
                  {field.type === "textarea" ? (
                    <Textarea
                      disabled={readOnly}
                      minH={28}
                      value={String(currentValue ?? "")}
                      onChange={(event) => setFieldDraft(field.key, event.target.value)}
                    />
                  ) : field.type === "select" ? (
                    <NativeSelect.Root disabled={readOnly}>
                      <NativeSelect.Field
                        value={String(currentValue ?? "")}
                        onChange={(event) => setFieldDraft(field.key, event.target.value)}
                      >
                        <option value="">Select…</option>
                        {(field.options ?? []).map((option) => <option key={option} value={option}>{option}</option>)}
                      </NativeSelect.Field>
                      <NativeSelect.Indicator />
                    </NativeSelect.Root>
                  ) : field.type === "file" ? (
                    <TaskFileField
                      readOnly={readOnly}
                      taskId={task._id}
                      field={field}
                      files={asFileList(currentValue)}
                      pending={pendingByField[field.key] ?? []}
                      saving={Boolean(saving)}
                      flash={flashByFieldKey[field.key] ?? null}
                      onPendingChange={(selected) => setPendingFiles(field.key, selected)}
                      onRemove={(files, target) => removeFile(taskId, field, files, target)}
                    />
                  ) : field.type === "range" ? (
                    <Stack gap={2} w="full">
                      <Slider.Root
                        disabled={readOnly}
                        colorPalette="blue"
                        min={field.min}
                        max={field.max}
                        step={field.step}
                        value={[typeof currentValue === "number" ? currentValue : Number(field.min ?? 0)]}
                        onValueChange={(e) => setFieldDraft(field.key, e.value[0] ?? null)}
                      >
                        <Slider.Control>
                          <Slider.Track>
                            <Slider.Range />
                          </Slider.Track>
                          <Slider.Thumb index={0}>
                            <Slider.HiddenInput />
                          </Slider.Thumb>
                        </Slider.Control>
                      </Slider.Root>
                      <Text fontSize="sm" color="fg.muted">Value: {typeof currentValue === "number" ? currentValue : "—"}</Text>
                    </Stack>
                  ) : (
                    <Input
                      disabled={readOnly}
                      type={field.type === "date"
                        ? "date"
                        : field.type === "url"
                          ? "url"
                          : field.type === "email"
                            ? "email"
                            : field.type === "number"
                              ? "number"
                              : field.type === "time"
                                ? "time"
                                : "text"}
                      min={field.min}
                      max={field.max}
                      step={field.step}
                      value={currentValue === null ? "" : String(currentValue)}
                      onChange={(event) => setFieldDraft(field.key, field.type === "number" ? (event.target.value === "" ? null : Number(event.target.value)) : event.target.value)}
                    />
                  )}
                </Field.Root>
              );
            })}

            <Stack gap={3} borderTopWidth="1px" borderColor="border" pt={4}>
              {/* When, then where it stands, read before what can be done
                  about it. The date is the one that matters: when it was
                  submitted, or failing that when it was last saved -- and
                  "submitted" only where that is still true, since a
                  withdrawn step keeps its submittedAt for the signatures. */}
              {(() => {
                const when = task.submittedAt !== null && (task.status === "awaiting_review" || task.status === "approved")
                  ? `Submitted ${formatDayTime(task.submittedAt)}.`
                  : task.lastSavedAt
                    ? `Saved ${formatDayTime(task.lastSavedAt)}.`
                    : null;
                const standing = task.status === "approved"
                  // A step nobody signs was never approved. Same stored
                  // status, different word. Nothing here offers a way back:
                  // an approved step cannot be reopened, by a reviewer or an
                  // admin (postponed, see the activities plan §37).
                  ? (needsReview ? "Approved." : "Done.")
                  : task.status === "awaiting_review"
                    ? `Waiting for ${task.reviewersPhrase ?? "a reviewer"} to sign off.`
                    : withdrawn
                      ? readOnly
                        ? `${subjectName ?? "The assignee"} took this back to change it. It will come back for approval when they submit again.`
                        : "Withdrawn. Change it and submit it again."
                      : readOnly
                        ? `${task.actorLabel} fills this in.`
                        : null;
                return when === null && standing === null ? null : (
                  <Stack gap={0.5}>
                    {when === null ? null : <Text fontSize="sm" color="fg.muted">{when}</Text>}
                    {standing === null ? null : <Text fontSize="sm" color="fg.muted">{standing}</Text>}
                  </Stack>
                );
              })()}

              <Flex flexWrap="wrap" align="center" gap={3}>
                {task.status === "approved" || readOnly && !(task.status === "awaiting_review" && task.viewerCanWithdraw) ? null
                  : task.status === "awaiting_review" ? (
                    // Taking it back, rather than editing under whoever is
                    // reading it. Refused by the backend once anybody has
                    // signed.
                    task.viewerCanWithdraw ? (
                      <Button variant="outline" loading={Boolean(saving)} onClick={() => setConfirming("withdraw")}>
                        <LuUndo2 />
                        Withdraw submission
                      </Button>
                    ) : null
                  ) : task.performedByOther ? (
                    <Button colorPalette="blue" loading={Boolean(saving)} onClick={() => void handleCompleteAsActor(task)}>
                      <LuCircleCheckBig />
                      {needsReview ? "Submit for approval" : "Complete step"}
                    </Button>
                  ) : (
                    <>
                      <Button variant="outline" loading={Boolean(saving)} onClick={() => void handleSave(task)}>
                        <LuSave />
                        Save draft
                      </Button>
                      <Button colorPalette="blue" disabled={Boolean(saving)} onClick={() => setConfirming("submit")}>
                        <LuFileText />
                        Submit step
                      </Button>
                    </>
                  )}
                {changePeople}
              </Flex>

            </Stack>

            {flash ? (
              <Box borderWidth="1px" borderColor="border" bg="bg.muted" px={4} py={3}>
                <Text fontSize="sm">{flash}</Text>
                {flashDetail ? (
                  <Text mt={1} fontSize="xs" fontFamily="mono" color="fg.muted">
                    {flashDetail}
                  </Text>
                ) : null}
              </Box>
            ) : null}
          </Stack>
        ) : null}

        {!(showForm && task.formSchema) && changePeople ? (
          <Flex>{changePeople}</Flex>
        ) : null}

        {task.actionableReviewers.length > 0 && task.status === "awaiting_review" ? (
          <Stack gap={3} borderTopWidth="1px" borderColor="border" pt={4}>
            {/* The role said once, in the heading, rather than on each button
                and again in the form. Several slots (rare: one person holding
                two) say it per slot instead. */}
            {/* Labelled as a field is, like everything else in the step, with
                the shield the app uses for "because of a role". */}
            <Text fontSize="sm" fontWeight="medium" display="inline-flex" alignItems="center" gap={1.5}>
              <Icon as={LuShieldUser} boxSize={4} color="fg.muted" />
              Your decision
              {task.actionableReviewers.length === 1
                ? ` as ${task.actionableReviewers[0]!.spec.kind === "person"
                  // A named reviewer's label is their name.
                  ? task.actionableReviewers[0]!.label
                  : task.actionableReviewers[0]!.label.toLowerCase()}`
                : ""}
            </Text>
            {task.actionableReviewers.map((slot, slotIndex) => {
              const slotKey = `${taskId}:${slotIndex}`;
              const reviewFields = task.reviewFormSchema?.fields ?? [];
              const open = openDecision?.key === slotKey;
              return (
                <Box key={slotKey}>
                  {open ? (
                    <Stack gap={3}>
                      <Text fontSize="sm" fontWeight="medium">
                        {openDecision.mode === "approve" ? "Approve" : "Request a revision"}
                        {task.actionableReviewers.length > 1 ? ` as ${slot.label}` : ""}
                      </Text>
                      {openDecision.mode === "approve" && reviewFields.length > 0 ? (
                        <Box borderWidth="1px" borderColor="border" rounded="md" bg="bg.muted" p={4}>
                          <ReviewFormFields
                            fields={reviewFields}
                            values={decisionValues}
                            onChange={(key, value) =>
                              setDecisionValues((current) => ({ ...current, [key]: value }))}
                          />
                        </Box>
                      ) : null}
                      <Textarea
                        // NOT readOnly: that describes the
                        // SUBMITTER's form. This is the
                        // reviewer's own comment, and the one
                        // moment they need it is exactly when
                        // the form beside it is locked.
                        disabled={activityClosed}
                        rows={3}
                        value={decisionComment}
                        placeholder={openDecision.mode === "approve" ? "Optional comment" : "What needs changing?"}
                        onChange={(event) => setDecisionComment(event.target.value)}
                      />
                      <HStack gap={3}>
                        <Button
                          colorPalette={openDecision.mode === "approve" ? "green" : "orange"}
                          onClick={() => void submitDecision(task._id, slot.spec, reviewFields)}
                        >
                          {openDecision.mode === "approve" ? "Approve" : "Request revision"}
                        </Button>
                        <Button variant="ghost" onClick={() => setOpenDecision(null)}>Cancel</Button>
                      </HStack>
                    </Stack>
                  ) : (
                    <HStack gap={3}>
                      <Button
                        size="sm"
                        colorPalette="green"
                        onClick={() => {
                          setOpenDecision({ key: slotKey, mode: "approve" });
                          setDecisionComment("");
                          setDecisionValues({});
                        }}
                      >
                        <LuCircleCheckBig />
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        colorPalette="orange"
                        onClick={() => {
                          setOpenDecision({ key: slotKey, mode: "revise" });
                          setDecisionComment("");
                          setDecisionValues({});
                        }}
                      >
                        <LuMessageSquareWarning />
                        Request revision
                      </Button>
                      {task.actionableReviewers.length > 1 ? (
                        <Text fontSize="xs" color="fg.muted">as {slot.label}</Text>
                      ) : null}
                    </HStack>
                  )}
                </Box>
              );
            })}
          </Stack>
        ) : null}

        {task.reviews.length > 0 ? (
          <ReviewHistory reviews={task.reviews} reviewFields={task.reviewFormSchema?.fields ?? []} />
        ) : null}
              </Stack>
            </Collapsible.Content>
          </Box>
        </Collapsible.Root>
        {/* Portalled, so nesting it in the card costs
            nothing in the DOM. Both actions are visible to
            somebody else the moment they happen: submitting
            puts work in a reviewer's queue and notifies them,
            withdrawing takes it out again. */}
        <Dialog.Root
          role="alertdialog"
          open={confirming !== null}
          onOpenChange={(event) => { if (!event.open) setConfirming(null); }}
        >
          <Portal>
            <Dialog.Backdrop />
            <Dialog.Positioner>
              <Dialog.Content maxW="md">
                <Dialog.Header>
                  {/* The step's own name: the body then
                      only has to say what happens. */}
                  <Dialog.Title>{task.titleSnapshot}</Dialog.Title>
                  <Dialog.CloseTrigger asChild>
                    <CloseButton size="sm" />
                  </Dialog.CloseTrigger>
                </Dialog.Header>
                <Dialog.Body>
                  <Text fontSize="sm">
                    {confirming === "withdraw"
                      ? "Your submission goes back to a draft only you can see, "
                        + `and ${task.reviewersPhrase ?? "the reviewer"} will be told it is no longer waiting for them. `
                        + "You can change it and submit it again."
                      : needsReview
                        ? `Your submission goes to ${task.reviewersPhrase ?? "a reviewer"} for approval, `
                          + "and they are notified. "
                          + (task.reviewMode === "all" && task.reviewers.length > 1
                            ? "Each of them needs to sign it off. "
                            : task.reviewers.length > 1 ? "One sign-off is enough. " : "")
                          + "You can withdraw it until someone has signed it off."
                        : "Submitting completes this step. Nobody needs to sign it off."}
                  </Text>
                </Dialog.Body>
                <Dialog.Footer>
                  <ButtonGroup gap={2}>
                    <Button variant="ghost" onClick={() => setConfirming(null)}>Cancel</Button>
                    <Button
                      colorPalette={confirming === "withdraw" ? "orange" : "blue"}
                      loading={Boolean(saving)}
                      onClick={() => {
                        const action = confirming;
                        setConfirming(null);
                        if (action === "withdraw") {
                          void handleWithdraw(task);
                        } else {
                          void handleSubmit(task);
                        }
                      }}
                    >
                      {confirming === "withdraw"
                        ? "Withdraw"
                        : needsReview ? "Submit for approval" : "Submit"}
                    </Button>
                  </ButtonGroup>
                </Dialog.Footer>
              </Dialog.Content>
            </Dialog.Positioner>
          </Portal>
        </Dialog.Root>
      </Timeline.Content>
    </Timeline.Item>
  );
}

// A step's stored status in the vocabulary the colours are defined in.
const STEP_PRESENTATION: Record<AssignmentTask["status"], string> = {
  not_started: "not_started",
  draft: "in_progress",
  awaiting_review: "awaiting_review",
  needs_revision: "needs_revision",
  approved: "completed",
};

function formatDate(timestamp: number) {
  return new Intl.DateTimeFormat("en-GB", { dateStyle: "medium" }).format(timestamp);
}

/** Who signed or sent back, in which role -- in words, not the stored keys. */
function reviewerRoleLabel(review: AssignmentTask["reviews"][number]): string {
  const spec = review.reviewerSpec;
  if (review.reviewerCapacity === "admin") {
    return "as an activity admin";
  }
  switch (spec.kind) {
    case "role":
      return {
        activityAdmin: "activity admin",
        hrAdmin: "HR admin",
        grantsAdmin: "grants admin",
        sysAdmin: "system admin",
      }[spec.role];
    case "supervisor":
      return review.reviewerCapacity === "deputyHead"
        ? "supervisor, as deputy head"
        : review.reviewerCapacity === "delegate"
          ? "supervisor, as delegate"
          : "supervisor";
    case "coSupervisor":
      return "co-supervisor";
    case "person":
      return "named reviewer";
  }
}

type StepReview = AssignmentTask["reviews"][number];

/**
 * Every decision on the step, newest first: a table, closed until opened, as
 * the activity's own history below the steps is.
 *
 * Kept although that history records each decision too. It records THAT
 * somebody approved or sent back; what they wrote -- the comment, and the
 * answers to the step's review form, an assessment for instance -- is kept
 * on the review alone, and this is the only place it is read.
 */
function ReviewHistory({ reviews, reviewFields }: { reviews: StepReview[]; reviewFields: FieldDefinition[] }) {
  const rows = [...reviews].sort((left, right) => right.createdAt - left.createdAt);
  const columns = [
    reviewColumn.accessor("createdAt", {
      header: "When",
      cell: ({ getValue }) => <Text whiteSpace="nowrap">{formatDayTime(getValue())}</Text>,
    }),
    reviewColumn.display({
      id: "who",
      header: "Who",
      cell: ({ row }) => (
        <Stack gap={0}>
          <Text>{row.original.reviewerName ?? "No longer at CeMM"}</Text>
          <Text fontSize="xs" color="fg.muted">{reviewerRoleLabel(row.original)}</Text>
        </Stack>
      ),
    }),
    reviewColumn.accessor("decision", {
      header: "Decision",
      cell: ({ getValue }) => (
        <Badge colorPalette={getValue() === "approved" ? "green" : "orange"} variant="subtle">
          {getValue() === "approved" ? "Approved" : "Needs revision"}
        </Badge>
      ),
    }),
    reviewColumn.display({
      id: "said",
      header: "Comment",
      // The structured outcome, read back against the schema the TASK
      // carries -- which is why a later edit to the catalogue cannot orphan
      // these answers.
      cell: ({ row }) => {
        const answers = reviewFields.flatMap((field) => {
          const answer = row.original.responseData?.[field.key];
          return answer === undefined || answer === null || answer === ""
            ? []
            : [{ label: field.label, value: typeof answer === "boolean" ? (answer ? "Yes" : "No") : String(answer) }];
        });
        return (
          <Stack gap={1}>
            {row.original.comment ? <Text whiteSpace="pre-wrap">{row.original.comment}</Text> : null}
            {answers.map((answer) => (
              <Text key={answer.label} fontSize="sm">
                <Text as="span" color="fg.muted">{answer.label}: </Text>
                {answer.value}
              </Text>
            ))}
            {!row.original.comment && answers.length === 0 ? <Text color="fg.muted">—</Text> : null}
          </Stack>
        );
      },
    }),
  ];

  return (
    <Collapsible.Root borderTopWidth="1px" borderColor="border" pt={2}>
        <Collapsible.Trigger asChild>
          <Button variant="plain" w="full" justifyContent="flex-start" px={0} gap={2}>
            <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="0.18em" color="fg.muted">
              Review history
            </Text>
            <Text as="span" fontSize="sm" color="fg.muted">({rows.length})</Text>
            <Collapsible.Indicator ms="auto" color="fg.muted" transition="transform 0.15s" _open={{ transform: "rotate(180deg)" }}>
              <LuChevronDown />
            </Collapsible.Indicator>
          </Button>
        </Collapsible.Trigger>
        <Collapsible.Content>
          <Box borderWidth="1px" borderColor="border" bg="bg">
            <DataTable
              data={rows}
              columns={columns}
              getRowId={(review) => review._id}
              size="sm"
              interactive={false}
            />
          </Box>
        </Collapsible.Content>
    </Collapsible.Root>
  );
}

const reviewColumn = createColumnHelper<StepReview>();

/**
 * A step completed by a record rather than by work.
 *
 * Either it already cites one -- automatically, because the roster confirmed
 * an attendance or somebody recorded a conference that matched -- or it offers
 * the records that would qualify. The offer matters: the person should not
 * have to work out which of their records the step will accept, and where
 * there are none the answer is to go and record the occasion, not to hunt for
 * a form that is not there.
 */
function CitedRecordBlock({ task }: { task: AssignmentTask }) {
  const effectivePersonId = useEffectivePersonId();
  const cite = useMutation(api.participations.citeParticipationForTask);
  const citable = useQuery(
    api.participations.listCitableFor,
    task.citedRecord === null
      ? { taskId: task._id, effectivePersonId: effectivePersonId as Id<"persons"> | null }
      : "skip",
  );
  const [chosen, setChosen] = useState<string>("");
  const [saving, setSaving] = useState(false);

  if (task.citedRecord !== null) {
    const record = task.citedRecord;
    const awaiting = task.status === "awaiting_review";

    return (
      <Box
        borderWidth="1px"
        borderColor={awaiting ? "border" : "green.200"}
        bg={awaiting ? "bg.muted" : "green.50"}
        px={5}
        py={4}
      >
        <Stack gap={3}>
          <HStack gap={2}>
            <Icon
              as={awaiting ? LuClock3 : LuCircleCheckBig}
              boxSize={4}
              color={awaiting ? "fg.muted" : "green.600"}
            />
            <Text fontSize="sm" fontWeight="medium">
              {awaiting ? "Cited for this step, waiting to be signed" : "Completed by this record"}
            </Text>
          </HStack>

          {/* Everything a signature is given against. A title and a date are
              enough to recognise the record and not enough to judge it: what
              was asked for was a role, in a period, of a kind. */}
          <Stack gap={1}>
            <Text fontSize="sm" fontWeight="semibold">{record.title}</Text>
            <RecordLine label="What">
              {participationRoleLabel(record.role)}
              {record.kind === null
                ? ""
                : ` · ${record.kind === "course" ? "course" : record.kind === "conference" ? "conference" : "other"}`}
            </RecordLine>
            <RecordLine label="When">
              {formatDay(record.startDay)}
              {record.endDay === null || record.endDay === record.startDay
                ? ""
                : ` – ${formatDay(record.endDay)}`}
            </RecordLine>
            {record.organiser === null || record.organiser === "" ? null : (
              <RecordLine label="Organiser">{record.organiser}</RecordLine>
            )}
            {record.location === null || record.location === "" ? null : (
              <RecordLine label="Where">{record.location}</RecordLine>
            )}
            {record.credits === null ? null : (
              <RecordLine label="ECTS">
                {record.credits}{record.grade === null || record.grade === "" ? "" : ` · grade ${record.grade}`}
              </RecordLine>
            )}
            {record.url === null || record.url === "" ? null : (
              <RecordLine label="Link">
                <TextLink>
                  <a href={record.url} target="_blank" rel="noreferrer">{record.url}</a>
                </TextLink>
              </RecordLine>
            )}
            {record.note === "" ? null : <RecordLine label="Note">{record.note}</RecordLine>}
          </Stack>

          {/* Principle 6, said out loud. The whole reason a requirement carries
              a reviewer is that self-reported data is unverified, so who
              entered this is part of what is being weighed. */}
          <Text fontSize="xs" color="fg.muted">
            {record.recordedBySelf
              ? "Entered by the person themselves."
              : "Entered by an administrator."}
          </Text>
        </Stack>
      </Box>
    );
  }

  if (task.status === "approved") {
    return null;
  }

  return (
    <Box borderWidth="1px" borderStyle="dashed" borderColor="border" bg="bg.muted" px={5} py={4}>
      <Stack gap={3}>
        <Text fontSize="sm" color="fg.muted">
          Nothing to fill in: this step is done once your attendance is confirmed.
        </Text>

        {citable === undefined ? (
          // Undefined for good once a record is cited (the query is skipped).
          task.citedRecord === null ? <Skeleton height="8" width="full" /> : null
        ) : citable.length === 0 ? (
          <Text fontSize="sm" color="fg.muted">
            Nothing on your record counts for this yet.
            {RECORD_ENABLED ? (
              <>
                {" "}You can add a conference or course on{" "}
                <TextLink><Link to="/activities" search={{ tab: "record" }}>My record</Link></TextLink>.
              </>
            ) : null}
          </Text>
        ) : (
          <HStack gap={3} wrap="wrap">
            <NativeSelect.Root size="sm" maxW="24rem">
              <NativeSelect.Field
                value={chosen}
                onChange={(event) => setChosen(event.target.value)}
              >
                <option value="">Choose one of your records…</option>
                {citable.map((row) => (
                  <option key={row._id} value={row._id}>
                    {row.external?.title ?? row.event?.title ?? "An occasion"} — {formatDay(row.startDay)}
                  </option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
            <Button
              size="sm"
              colorPalette="blue"
              loading={saving}
              disabled={chosen === ""}
              onClick={() => {
                setSaving(true);
                void (async () => {
                  try {
                    await cite({
                      taskId: task._id,
                      participationId: chosen as Id<"participations">,
                      effectivePersonId: effectivePersonId as Id<"persons"> | null,
                    });
                  } catch (error) {
                    const { message, detail } = readableError(error, "That record could not be used");
                    toastError(message, detail);
                  } finally {
                    setSaving(false);
                  }
                })();
              }}
            >
              Use it
            </Button>
          </HStack>
        )}
      </Stack>
    </Box>
  );
}

function RecordLine({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <HStack gap={2} align="baseline">
      <Text minW="5.5rem" fontSize="xs" color="fg.muted">{label}</Text>
      <Text fontSize="sm">{children}</Text>
    </HStack>
  );
}
